/* ============================================================================
   assets/card-grid.js
   ----------------------------------------------------------------------------
   Behaviour for snippets/card-grid.liquid — fast, delegated async add-to-cart
   for Pokémon TCG single-card grids (most variants have inventory = 1, so we
   treat every add as a potential race and handle the 422 "sold out" response
   gracefully, in place, without a page reload).

   Public API (window.PCG):
     PCG.refresh()          - re-bind the delegated submit listener
     PCG.add(formEl)         - programmatically submit one card's form
     PCG.refreshCartCount()  - re-pull /cart.js and update any cart badges
   ========================================================================== */

(function () {
  'use strict';

  var STATE_RESET_DELAY = 1600;   // ms the "Added" state stays before reverting
  var ERROR_RESET_DELAY = 2200;   // ms the error/shake state stays before reverting

  /* --------------------------------------------------------------------- */
  /* Helpers                                                                */
  /* --------------------------------------------------------------------- */

  function qs(sel, ctx) {
    return (ctx || document).querySelector(sel);
  }

  function qsa(sel, ctx) {
    return Array.prototype.slice.call((ctx || document).querySelectorAll(sel));
  }

  function setButtonState(button, state, label) {
    if (!button) return;
    button.setAttribute('data-state', state);
    var labelEl = button.querySelector('.pcg-card__add-label');
    if (labelEl && label) {
      labelEl.textContent = label;
    }
  }

  function resetButtonState(button) {
    if (!button) return;
    var defaultLabel = button.getAttribute('data-default-label') || 'Add to cart';
    button.removeAttribute('data-state');
    var labelEl = button.querySelector('.pcg-card__add-label');
    if (labelEl) labelEl.textContent = defaultLabel;
  }

  function markCardSoldOut(card, button) {
    if (!card) return;
    card.classList.add('is-soldout');
    if (button) {
      button.disabled = true;
      button.setAttribute('aria-disabled', 'true');
      button.setAttribute('data-default-label', 'Sold out');
      var labelEl = button.querySelector('.pcg-card__add-label');
      if (labelEl) labelEl.textContent = 'Sold out';
    }
  }

  /* --------------------------------------------------------------------- */
  /* Cart count badge refresh                                              */
  /* --------------------------------------------------------------------- */

  function refreshCartCount() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (res) {
        if (!res.ok) throw new Error('cart.js request failed');
        return res.json();
      })
      .then(function (cart) {
        var count = cart && typeof cart.item_count === 'number' ? cart.item_count : 0;
        var targets = qsa('[data-cart-count], .cart-count, #cart-count-bubble');
        targets.forEach(function (el) {
          el.textContent = count;
          el.setAttribute('data-cart-count-value', String(count));
        });
        return cart;
      })
      .catch(function () {
        /* Non-fatal — the add itself already succeeded. */
      });
  }

  /* --------------------------------------------------------------------- */
  /* Add-to-cart submit handler                                             */
  /* --------------------------------------------------------------------- */

  function addFromForm(form) {
    if (!form || form.getAttribute('data-pcg-busy') === 'true') {
      return Promise.resolve();
    }

    var card = form.closest('.pcg-card');
    var button = form.querySelector('.pcg-card__add');

    if (button && button.disabled) {
      return Promise.resolve();
    }

    form.setAttribute('data-pcg-busy', 'true');
    setButtonState(button, 'loading');

    var formData = new FormData(form);

    return fetch('/cart/add', {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: formData,
    })
      .then(function (response) {
        return response.json().then(function (data) {
          return { ok: response.ok, status: response.status, data: data };
        });
      })
      .then(function (result) {
        form.removeAttribute('data-pcg-busy');

        if (!result.ok) {
          // Typical low-stock race: someone else bought the last copy.
          // Shopify returns 422 with a message/description.
          setButtonState(button, 'error', 'Sold out');
          if (result.status === 422) {
            markCardSoldOut(card, button);
          }
          window.setTimeout(function () {
            if (!card || !card.classList.contains('is-soldout')) {
              resetButtonState(button);
            }
          }, ERROR_RESET_DELAY);
          return result;
        }

        setButtonState(button, 'added', '✓ Added');

        document.dispatchEvent(
          new CustomEvent('cart:added', {
            bubbles: true,
            detail: { form: form, card: card, response: result.data },
          })
        );

        refreshCartCount();

        // Every card is a single unique item (quantity = 1), so once it's
        // been added there's nothing left to add — permanently grey out
        // the button instead of resetting it back to "Add to cart".
        // Note: we deliberately clear data-state (rather than leaving it
        // as "added") so the green success colour doesn't override the
        // grey :disabled styling below.
        window.setTimeout(function () {
          if (button) {
            button.disabled = true;
            button.setAttribute('aria-disabled', 'true');
            button.removeAttribute('data-state');
            var labelEl = button.querySelector('.pcg-card__add-label');
            if (labelEl) labelEl.textContent = 'Added to cart';
          }
        }, STATE_RESET_DELAY);

        return result;
      })
      .catch(function (err) {
        form.removeAttribute('data-pcg-busy');
        setButtonState(button, 'error', 'Try again');
        window.setTimeout(function () {
          resetButtonState(button);
        }, ERROR_RESET_DELAY);
        return { ok: false, error: err };
      });
  }

  /* --------------------------------------------------------------------- */
  /* Delegated listener — one listener handles every card in every grid,   */
  /* so performance stays flat no matter how many cards are rendered.      */
  /* --------------------------------------------------------------------- */

  function onGridSubmit(evt) {
    var form = evt.target.closest && evt.target.closest('[data-pcg-card]');
    if (!form) return;
    evt.preventDefault();
    addFromForm(form);
  }

  function bind() {
    document.removeEventListener('submit', onGridSubmit, true);
    document.addEventListener('submit', onGridSubmit, true);
  }

  /* --------------------------------------------------------------------- */
  /* Carousel — jumps a full page of N cards at a time, loops forever,     */
  /* pauses on hover / keyboard focus.                                      */
  /* --------------------------------------------------------------------- */

  function initCarousel(root) {
    if (!root || root.getAttribute('data-pcg-bound') === 'true') return;
    root.setAttribute('data-pcg-bound', 'true');

    var track = qs('[data-pcg-track]', root);
    if (!track) return;

    var pages = qsa('.pcg-carousel__page', track);
    if (pages.length <= 1) return; // nothing to rotate

    var interval = parseInt(root.getAttribute('data-pcg-interval'), 10) || 5000;
    var pageIndex = 0;
    var timerId = null;
    var paused = false;

    function goToPage(idx) {
      pageIndex = idx;
      track.style.transform = 'translateX(-' + (pageIndex * 100) + '%)';
    }

    function next() {
      if (paused) return;
      var nextIndex = (pageIndex + 1) % pages.length;
      goToPage(nextIndex);
    }

    function start() {
      stop();
      timerId = window.setInterval(next, interval);
    }

    function stop() {
      if (timerId) {
        window.clearInterval(timerId);
        timerId = null;
      }
    }

    function pause() {
      paused = true;
    }

    function resume() {
      paused = false;
    }

    root.addEventListener('mouseenter', pause);
    root.addEventListener('mouseleave', resume);
    root.addEventListener('focusin', pause);
    root.addEventListener('focusout', resume);

    goToPage(0);
    start();
  }

  function initAllCarousels() {
    qsa('[data-pcg-carousel]').forEach(initCarousel);
  }

  /* --------------------------------------------------------------------- */
  /* Public API                                                             */
  /* --------------------------------------------------------------------- */

  window.PCG = window.PCG || {};
  window.PCG.refresh = bind;
  window.PCG.add = addFromForm;
  window.PCG.refreshCartCount = refreshCartCount;
  window.PCG.initCarousels = initAllCarousels;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      bind();
      refreshCartCount();
      initAllCarousels();
    });
  } else {
    bind();
    refreshCartCount();
    initAllCarousels();
  }
})();


